/* eslint-disable import/no-cycle */
/* eslint-disable no-continue */
/* eslint-disable no-plusplus */
import { Uploader, UploadEnv, type Sentry } from '@meitu/upload';
import { setUploadErrorToast } from '$/store/useUploadErrorToastStore';
import useUploadThreadStore, { removeThread, setUploadThread } from '$/store/useUploadThreadStore';
import { message } from '$/components/Message';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useLanguageStore } from '$/store/languageStore';
import { replaceTemplateParams, getImgSizeByFile, getUrlSearchParams } from '$/utils';
import useSopStore from '$/store/useSopStore';
import { checkFileApi, uploadSign, type UploadSignParams } from '../api/server';
import { textSafeCheck } from '../domains/teamAgent/teamAgentService';
import { videoCompress } from '../domains/workFlowV3/workFlowV3Service';
import { mtstatTrack } from './mtstat';
import { IMAGE_UPLOAD_SIZE_LIMIT_MB } from './mediaUploadLimits';
import { getUploadCompleteLocationTrackParams } from './uploadTrack';
import { getVideoCoverUrl } from './videoCoverUrl';
import { uploadItemsInBatches } from './uploadBatch';

export { getVideoCoverUrl, getVideoFrameUrl, resolveVideoCoverUrl } from './videoCoverUrl';

interface UploadOptions {
  app?: string;
  headers?: Record<string, string>;
  suffix?: string;
  prefix?: string;
  type?: string;
  hideProgressMessage?: boolean;
}
const UPLOAD_APP = 'RoboNeo';
const UPLOAD_TYPE = 'roboneo_private_web';
let uploadThread = 0;
class Upload {
  private static instance: Upload;

  private uploader!: Uploader;

  private isInitialized: boolean = false;

  public uploadTaskId: number = 0;

  private constructor() {
    // 私有构造函数，防止外部直接实例化
  }

  public static getInstance(): Upload {
    const instance = new Upload();
    instance.uploadTaskId = ++uploadThread;
    return instance;
  }

  /**
   * 初始化上传器
   * @param options 初始化配置
   */
  public init(options: { debug?: boolean; } = {}) {
    const { env, sentryConfig } = useOfficialStore.getState();
    const { uploadConfig } = useSopStore.getState();
    this.uploader = new Uploader({
      debug: options.debug || false,
      env: ['release', 'beta'].includes(env) ? UploadEnv.Release : UploadEnv.Test,
      sentry: sentryConfig as unknown as Sentry,
      sign(params) {
        console.log('sign params', params);
        return uploadConfig?.uploadSign
          ? uploadConfig?.uploadSign(params)
          : new Promise((resolve, reject) => {
          // console.warn('业务方请求参数签名接口完成！')

            uploadSign({
              parameter: {
                path_scene: 'uploadpolicy',
                upload_version: params.version,
                ...params,
              },
            } as UploadSignParams).then((res) => {
              console.log('uploadSign res', res);
              if (res.data.error_code === 0) {
                resolve({
                  ...params,
                  ...res.data.parameter,
                });
              } else {
              // uploadPolicyFailCb?.({
              //   ...params,
              // });
              // eslint-disable-next-line prefer-promise-reject-errors
                reject(res.data);
              }
            }).catch((error) => {
            // uploadPolicyFailCb?.({
            //   ...params,
            // });
            // eslint-disable-next-line prefer-promise-reject-errors
              reject(error);
            });
          });
      },
    });
  }

  /** 按上传 SDK 的单批上限顺序上传文件，并合并为原有批量返回结构。 */
  public async uploadMultiple(files: File[], options: UploadOptions = {}, progressChange?: (data: number) => void) {
    const self = this;

    const { type = UPLOAD_TYPE, app = UPLOAD_APP } = options;
    const results = await uploadItemsInBatches(files, (batchFiles) => (
      this.uploader.uploadMultiple(batchFiles, {
        app,
        type: type || '',
      }, {
        progress(result) {
          progressChange && progressChange(result.total.percent + 20);
        },
        failure(error, index) {
          if (options.hideProgressMessage) return;
          console.log('failure', error, index, useUploadThreadStore.getState().cancelTaskId && (useUploadThreadStore.getState().cancelTaskId, String(self.uploadTaskId)));
          if (self.uploadTaskId && useUploadThreadStore.getState().cancelTaskId && (useUploadThreadStore.getState().cancelTaskId !== String(self.uploadTaskId))) return;
          setUploadThread(String(self.uploadTaskId), {
            done: useUploadThreadStore.getState().uploadThread[self.uploadTaskId].done + 1,
          });
        },
        complete(result) {
          console.log('complete', result);
          if (options.hideProgressMessage) return;
          if (self.uploadTaskId && useUploadThreadStore.getState().cancelTaskId && (useUploadThreadStore.getState().cancelTaskId !== String(self.uploadTaskId))) return;
          setUploadThread(String(self.uploadTaskId), {
            done: useUploadThreadStore.getState().uploadThread[self.uploadTaskId].done + 1,
          });
        },
      })
    ));

    if (!results) {
      return null;
    }
    return results.map((item) => ({ uri: item.key, url: item.previewUrl }));
  }

  /**
   * 上传成功回调
   * @param file 上传成功的文件
   * @param response 服务器响应
   */
  public onUploadSuccess(file: File, response: any) {
    console.log('Upload success:', file, response, this);
    // 这里可以添加自定义的成功处理逻辑
  }

  /**
   * 上传失败回调
   * @param file 上传失败的文件
   * @param error 错误信息
   */
  public onUploadError(file: File, error: any) {
    console.error('Upload error:', file, error, this);
    // 这里可以添加自定义的错误处理逻辑
  }

  /**
   * 获取上传进度
   * @param file 正在上传的文件
   * @param progress 上传进度（0-100）
   */
  public onUploadProgress(file: File, progress: number) {
    console.log(`Upload progress for ${file.name}: ${progress}%`, this);
    // 这里可以添加自定义的进度处理逻辑
  }

  public onCancelUpload() {
    this.uploader.cancel(); // 暂停或取消上传
  }
}

// 导出类型
export type { UploadOptions };

export default Upload;

export type FileType = 'image' | 'video' | 'document' | 'audio';

export enum FileTypeEnum {
  Image = 'image',
  Video = 'video',
  Document = 'document',
  Audio = 'audio',
}

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
export const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const fileSuffixMap = {
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/msword': 'doc',
  'application/excel': 'xls',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'text/plain': 'txt',
  'application/pdf': 'pdf',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'text/csv': 'csv',
  'text/markdown': 'md',
};
export const FILE_TYPES = Object.keys(fileSuffixMap);
export const AUDIO_TYPES = [
  'audio/x-m4a',
  // 'audio/mp4',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
];
// const FILE_NO_AUDIO_TYPE_SUFFIX_MAP = FILE_TYPES.filter((type) => !AUDIO_TYPES.includes(type));
/** 图片/视频上传最长边上限（px），与模型入参图最大边能力对齐。 */
export const MAX_SIDE = 6000;
export const MAX_IMAGE_SIZE = IMAGE_UPLOAD_SIZE_LIMIT_MB;
export const MAX_VIDEO_SIZE = 500;
export const MAX_FILE_SIZE = 10;
export const MAX_AUDIO_SIZE = 500;
const MAX_IMAGE_NUMS = 10;
const MAX_VIDEO_NUMS = 1;
const MAX_FILE_NUMS = 1;
const MAX_AUDIO_NUMS = 1;
interface InputOptions {
  type: FileTypeEnum;
  accept?: string;
  maxSize?: number;
  maxSide?: number;
  importCount?: number;
  isCheckSafe?: boolean;
  isVideoCompress?: boolean;
  hideProgressMessage?: boolean;
  onUploadError?: (errorList: ErrorListInfo, errorMessage: CallBackErrorMessage[]) => void;
  from?: 'canvas' | 'input' | 'workflow';
  nodeId?: string;
}

interface HandleClickUploadFileOptions extends InputOptions {
  location?: LocationOptions;
  callback: (files: Array<CallbackFiles>) => void;
  /** 在通用格式校验和上传前过滤业务不接受的文件，返回空数组时终止本次上传。 */
  beforeUpload?: (files: File[]) => File[] | Promise<File[]>;
  changeBeforeUpload?: (file: File[]) => void;
  getUploaderInstance?: (uploadInstance: Upload) => void;
  progressChange?: (data: number) => void;
  uploadHandler?: (fileList: IFileList[]) => Promise<{ uri: string; url: string | undefined; size?: number; }[]>;
}

/**
 * 创建文件上传输入框的便捷函数
 * @param options 上传配置选项对象，包含所有参数
 * @returns HTMLInputElement 创建的文件输入框元素
 *
 * @example
 * // 基础用法 - 上传图片
 * const imageInput = handleClickUploadFile({
 *   type: FileTypeEnum.Image,
 *   callback: (files) => console.log('上传完成:', files)
 * });
 * document.body.appendChild(imageInput);
 * imageInput.click(); // 触发文件选择
 *
 * @example
 * // 高级用法 - 自定义配置
 * const videoInput = handleClickUploadFile({
 *   type: FileTypeEnum.Video,
 *   maxSize: 50, // 50MB
 *   importCount: 3, // 最多3个文件
 *   isCheckSafe: false, // 跳过安全检查
 *   callback: (files) => console.log('视频上传完成:', files),
 *   changeBeforeUpload: (files) => console.log('文件预处理:', files),
 *   getUploaderInstance: (uploader) => console.log('上传实例:', uploader)
 * });
 */
export const handleClickUploadFile = (options: HandleClickUploadFileOptions) => {
  const {
    type = 'image',
    location,
    accept: _accept,
    importCount: _importCount,
    maxSize: _maxSize,
    maxSide = MAX_SIDE,
    isCheckSafe = true,
    isVideoCompress = false,
    hideProgressMessage = false,
    onUploadError = () => {},
    from,
    nodeId,
    callback,
    beforeUpload,
    changeBeforeUpload,
    getUploaderInstance,
    progressChange,
    uploadHandler,
  } = options;
  let accept: string;
  if (type === 'image') {
    accept = _accept ?? IMAGE_TYPES.join(',');
  } else if (type === 'video') {
    accept = _accept ?? VIDEO_TYPES.join(',');
  } else if (type === 'audio') {
    accept = _accept ?? AUDIO_TYPES.join(',');
  } else {
    accept = _accept ?? FILE_TYPES.join(',');
  }
  let importCount: number;
  if (type === 'image') {
    importCount = _importCount ?? MAX_IMAGE_NUMS;
  } else if (type === 'video') {
    importCount = _importCount ?? MAX_VIDEO_NUMS;
  } else if (type === 'audio') {
    importCount = _importCount ?? MAX_AUDIO_NUMS;
  } else {
    importCount = _importCount ?? MAX_FILE_NUMS;
  }
  let maxSize: number;
  if (type === 'image') {
    maxSize = _maxSize ?? MAX_IMAGE_SIZE;
  } else if (type === 'video') {
    maxSize = _maxSize ?? MAX_VIDEO_SIZE;
  } else {
    maxSize = _maxSize ?? MAX_FILE_SIZE;
  }
  const input = document.createElement('input');

  input.type = 'file';
  input.multiple = importCount > 1;
  input.accept = accept;
  input.style.display = 'none';
  document.body.appendChild(input);
  let hasChange = false;
  function changeHandler(e: Event) {
    if (hasChange) return;
    hasChange = true;
    const { files } = e.target as HTMLInputElement;
    if (!files?.length) return;
    console.log('handleClickUploadFile files', files);
    // 兼容iOS14
    document.body.removeChild(input);
    dealUploadFile({
      location,
      files: Array.from(files),
      controlImagesOptions: {
        accept: type === 'image' ? accept.split(',') : [],
        maxSize,
        maxSide,
        importCount,
      },
      controlVideosOptions: {
        accept: type === 'video' ? accept.split(',') : [],
        maxSize,
        maxSide,
        importCount,
      },
      controlFilesOptions: {
        accept: type === 'document' ? accept.split(',') : [],
        maxSize,
        importCount,
      },
      controlAudiosOptions: {
        accept: type === 'audio' ? accept.split(',') : [],
        maxSize,
        importCount,
      },
      isCheckSafe,
      isVideoCompress,
      from,
      nodeId,
    }, {
      callback,
      hideProgressMessage,
      beforeUpload,
      changeBeforeUpload,
      getUploaderInstance,
      onUploadError,
      progressChange,
      uploadHandler,
    });
  }
  // 兼容iOS14 必须延迟绑定否则不触发
  setTimeout(() => {
    input.addEventListener('change', changeHandler);
    // 兼容iOS14
    input.addEventListener('input', changeHandler);
  });
  return input;
};
export function formatFileType(file: File) {
  const { type } = file;
  if (type.startsWith(FileTypeEnum.Image)) {
    return FileTypeEnum.Image;
  }
  if (type.startsWith(FileTypeEnum.Video)) {
    return FileTypeEnum.Video;
  }
  if (type.startsWith(FileTypeEnum.Audio)) {
    return FileTypeEnum.Audio;
  }
  return FileTypeEnum.Document;
}
export function getFileSuffix(file: File) {
  const { type } = file;
  return type.split('/')[1];
}

export function getDocumentSuffix(file: File) {
  const type = file.type as keyof typeof fileSuffixMap;
  return fileSuffixMap[type];
}

export function getDocumentSuffixByType(type: string) {
  const suffix = fileSuffixMap[type as keyof typeof fileSuffixMap];
  return suffix || type.split('/')[1];
}

export function getSuffixByUrl(url: string) {
  // 移除URL中的查询参数
  const urlWithoutParams = url.split('?')[0];
  // 提取文件后缀
  return urlWithoutParams.split('.').pop() || '';
}

export function getFileName(file: File) {
  const { name } = file;
  return file.name.substring(0, name.lastIndexOf('.'));
}
export function checkSize(file: File | Blob, maxSize: number) {
  const { size } = file;
  console.log('size', size, maxSize * 1024 * 1024);
  return size <= maxSize * 1024 * 1024;
}

export function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export async function removeExif(file: File): Promise<File> {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<File>(async (resolve) => {
    try {
      const img = await fileToImage(file);

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        const fixedFile = new File([blob!], file.name, { type: file.type });
        resolve(fixedFile);
      }, file.type);
    } catch (error) {
      resolve(file);
    }
  });
}

// interface ControlImagesOptions {
//   accept?: string[];
//   maxSize?: number;
//   maxSide?: number;
//   importCount?: number;
// }
interface ControlOptions {
  accept?: string[];
  maxSize?: number; // 最大存储空间 默认 500m
  maxSide?: number;
  importCount?: number;// 最大数量默认1
  maxTotal?: number; // 该类型固定总上限（用于 toast 展示）
  currentVideoCount?: number; // 当前已有视频数（用于判断组合提示）
}
export type LocationOptions =
  | 'DIRECTOR_CONSOLE'
  | 'workflow_chat'
  | 'agent_chat'
  | 'official_page'
  | 'home_page'
  | 'home_page_teams'
  | 'chat_list_teams'
  | 'canvas_teams'
  | 'publish_pop'
  | 'workflow_teams_left'
  | 'workflow_teams_add'
  | 'workflow_teams_node'
  | 'workflow_teams_drag'
  | 'role_reference'
  | 'role_clothing'
  | 'role_scene'
  | 'role_accessory';

interface FilesInfo {
  files: File[];
  controlImagesOptions?: ControlOptions;
  controlVideosOptions?: ControlOptions;
  controlFilesOptions?: ControlOptions;
  controlAudiosOptions?: ControlOptions;
  isCheckSafe?: boolean;
  isVideoCompress?: boolean;
  formateErrorText?: string;
  from?: 'canvas' | 'input' | 'workflow';
  nodeId?: string;
  location?: LocationOptions;
}

export type SourceType = 'input' | 'canvas' | 'callword' | 'quote' | 'official_page' | 'asset' | 'ai_tools';

export interface ImageInfo {
  id?: string; // 唯一id
  url: string;
  name: string;
  source: SourceType;
  width?: number;
  height?: number;
  description?: string;
}
export interface VideoInfo {
  id?: string;
  url: string;
  name: string;
  coverUrl?: string;
  source: SourceType;
  /** 视频上传后可选带上本地文件大小（字节），一般取自 File.size */
  size?: number;
  description?: string;
}

export interface FileInfo {
  id?: string;
  name: string;
  url: string;
  coverUrl?: string;
  source: SourceType;
  suffix?: string;
  /** 文档类型上传后可选带上本地文件大小（字节），一般取自 File.size */
  size?: number;
  description?: string;
}

export interface AudioInfo {
  id?: string;
  url: string;
  source: SourceType;
  description?: string;

}

// 封装点击创建input导入图片or视频 监听input的change事件
export interface CallbackFiles extends ImageInfo, VideoInfo, FileInfo, AudioInfo {
  index?: string;
  url: string;
  type: FileType;
  name: string;
  suffix?: string;
  width?: number;
  height?: number;
  size?: number;
  compressUrl?: string;
}
export interface IFileList {
  file: File;
  suffix: string;
  type: string;
  width?: number;
  height?: number;
}
export interface ErrorListInfo {
  formateError: string[];
  imageSizeError: string[];
  videoSizeError: string[];
  audioSizeError: string[];
  imageImportCountError: string[];
  videoImportCountError: string[];
  fileSizeError: string[];
  fileImportCountError: string[];
  audioImportCountError: string[];
  uploadError: string[];
  checkSafeError: string[];
  [key: string]: string[];
}
export interface ErrorMessage {
  message: {
    key: string;
    params?: Record<string, any>;
  };
  fileNum: number;
}

export interface CallBackErrorMessage {
  message: string;
  fileNum: number;
}

/** 上传失败/校验失败时随 Sentry 上报的上下文（不含 File 本体，仅元信息） */
export interface UploadErrorDiagnosticContext {
  location?: string;
  from?: string;
  nodeId?: string;
  failurePhase?:
  | 'validation_no_upload'
  | 'upload_all_failed'
  | 'upload_partial'
  | 'post_check_safe'
  | 'exception'
  | 'unknown';
  uploadTimeMs?: number;
  filterFileSummary?: Array<{ type: string; suffix: string; size: number; }>;
  uploadResSummary?: Array<{ index: number; hasUrl: boolean; uriLen: number; }>;
  /** 本次触发展示的 i18n key，逗号分隔 */
  i18nErrorKeys?: string;
}

/**
 * 将 errorList 中非空项压缩为便于 Sentry extra 展示的桶信息。
 */
function summarizeErrorListBuckets(errorList: ErrorListInfo): Record<string, { count: number; samples: string[]; }> {
  const out: Record<string, { count: number; samples: string[]; }> = {};
  (Object.keys(errorList) as (keyof ErrorListInfo)[]).forEach((key) => {
    const arr = errorList[key];
    if (Array.isArray(arr) && arr.length) {
      out[String(key)] = { count: arr.length, samples: arr.slice(0, 5) };
    }
  });
  return out;
}

/**
 * 从待上传列表提取类型/后缀/体积，用于问题排查。
 */
function buildFilterFileBrief(filterFile: IFileList[]) {
  return filterFile.map((f) => ({
    type: f.type,
    suffix: f.suffix,
    size: f.file.size,
  }));
}

/**
 * 从 SDK 合并后的上传结果提取是否带 url、uri 长度，用于区分「无条数」与「有条数但无 previewUrl」。
 */
function buildUploadResBrief(uploadRes: { uri: string; url: string | undefined; size?: number; }[]) {
  return uploadRes.map((r, index) => ({
    index,
    hasUrl: Boolean(r?.url),
    uriLen: r?.uri ? String(r.uri).length : 0,
  }));
}

/**
 * 使用宿主在 useOfficialStore.sentryConfig 中注入的 captureMessage 上报上传相关异常；未配置或调用抛错时不影响主流程。
 */
function reportUploadFailureToSentry(
  headline: string,
  payload: {
    errorList?: ErrorListInfo;
    diagnostic?: UploadErrorDiagnosticContext;
    caughtError?: unknown;
  },
) {
  const captureMessage = useOfficialStore.getState().sentryConfig?.captureMessage;
  if (typeof captureMessage !== 'function') return;
  const { errorList, diagnostic, caughtError } = payload;
  const errorBuckets = errorList ? summarizeErrorListBuckets(errorList) : {};
  const uid = useOfficialStore.getState().userInfo?.uid ?? '';
  try {
    captureMessage(headline, {
      level: 'warning',
      tags: {
        roboneo_upload: '1',
        upload_phase: diagnostic?.failurePhase ?? 'unknown',
        upload_location: diagnostic?.location ?? '',
        upload_from: diagnostic?.from ?? '',
        ...(uid ? { uid } : {}),
      },
      extra: {
        uid,
        errorBuckets,
        diagnostic: diagnostic ?? {},
        ...(caughtError !== undefined
          ? {
            thrown:
                caughtError instanceof Error
                  ? { name: caughtError.name, message: caughtError.message, stack: caughtError.stack }
                  : { message: String(caughtError) },
          }
          : {}),
      },
    });
  } catch {
    // 静默失败
  }
}

export function getUploadErrorMessage(errorList: ErrorListInfo, {
  controlImagesOptions, controlVideosOptions, controlFilesOptions, formateErrorText, controlAudiosOptions,
}: {
  controlImagesOptions: ControlOptions;

  controlVideosOptions: ControlOptions;
  controlFilesOptions: ControlOptions;
  formateErrorText?: string;
  controlAudiosOptions?: ControlOptions;
}, threadId: string, onUploadError?: (errorList: ErrorListInfo, errorMessage: CallBackErrorMessage[]) => void, diagnosticContext?: UploadErrorDiagnosticContext) {
  const errorMessage: ErrorMessage[] = [];
  const { languageStore } = useLanguageStore.getState();
  Object.keys(errorList).forEach((key) => {
    const fileList = errorList[key].length;
    switch (key) {
      case 'formateError':
        fileList && errorMessage.push({
          message: {
            key: formateErrorText || 'roboneo_file_toast1',
          },
          fileNum: fileList,
        });
        break;

      case 'imageSizeError':
        fileList && errorMessage.push({
          message: {
            key: 'RoboNeo_Web_upload_image_size_maximum',
            params: {
              max_size: controlImagesOptions.maxSize,
            },
          },
          fileNum: fileList,
        });
        break;

      case 'videoSizeError':
        fileList && errorMessage.push({
          message: {
            key: 'RoboNeo_Web_upload_video_size_maximum',
            params: {
              max_size: controlVideosOptions.maxSize,
            },
          },
          fileNum: fileList,
        });

        break;
      case 'fileSizeError':
        fileList && errorMessage.push({
          message: {
            key: 'roboneo_file_size',
            params: {
              max_size: controlFilesOptions.maxSize,
            },
          },

          fileNum: fileList,
        });

        break;
      case 'fileImportCountError':
        fileList && errorMessage.push({
          message: {
            key: 'roboneo_file_limit',
          },
          fileNum: fileList,
        });

        break;

      case 'imageImportCountError':
        fileList && errorMessage.push({
          message: controlImagesOptions.currentVideoCount
            ? {
              key: 'roboneo_chat_resources_num_limit_tips',
              params: {
                img_num: controlImagesOptions.maxTotal,
                video_num: controlImagesOptions.currentVideoCount,
              },
            }
            : {
              key: 'RoboNeo_Web_upload_image_maximum',
              params: {
                max_num: controlImagesOptions.maxTotal || controlImagesOptions.importCount,
              },
            },
          fileNum: fileList,
        });
        break;

      case 'videoImportCountError':
        fileList && errorMessage.push({
          message: {
            key: (controlVideosOptions.importCount && controlVideosOptions.importCount > 1 ? 'RoboNeo_Web_upload_video_maximum' : 'RoboNeo_Web_upload_video_maximum_one'),
            params: {
              max_num: controlVideosOptions.importCount,
            },
          },
          fileNum: fileList,
        });
        break;

      case 'audioSizeError':
        fileList && errorMessage.push({
          message: {
            key: 'RoboNeo_Web_upload_audio_size_maximum',
            params: {
              max_size: controlAudiosOptions?.maxSize,
            },
          },
          fileNum: fileList,
        });
        break;

      case 'audioImportCountError':
        fileList && errorMessage.push({
          message: {
            key: 'RoboNeo_Web_upload_audio_max',
            params: {
              max_num: controlAudiosOptions?.importCount,
            },
          },
          fileNum: fileList,
        });
        break;

      case 'uploadError':
        fileList && errorMessage.push({
          message: {
            key: 'RoboNeo_Web_upload_fail_text',
          },
          fileNum: fileList,
        });

        break;
      case 'checkSafeError':
        fileList && errorMessage.push({
          message: {
            key: 'RoboNeo_Web_upload_content_warning_text',
          },
          fileNum: fileList,
        });

        break;

      default:
        break;
    }
  });
  if (errorMessage.length && useUploadThreadStore.getState().cancelTaskId !== threadId) {
    setUploadErrorToast(true, errorMessage);
    const _errorMessage = errorMessage.map((item) => {
      const _message = (languageStore.smartcatTranslationText as any)((v: any) => v[item.message.key], item.message.params);
      return {
        message: replaceTemplateParams(_message, item.message.params),
        fileNum: item.fileNum,
      };
    });
    reportUploadFailureToSentry('[RoboNeo] upload user-facing errors', {
      errorList,
      diagnostic: {
        ...diagnosticContext,
        i18nErrorKeys: errorMessage.map((item) => item.message.key).join(','),
      },
    });
    onUploadError?.(errorList, _errorMessage);
  }
  return errorMessage;
}

/**
 * 初始化文件分类和错误列表
 */
export const initializeFileProcessing = (files: File[]) => {
  const imageList: IFileList[] = [];
  const videoList: IFileList[] = [];
  const fileList: IFileList[] = [];
  const errorList: ErrorListInfo = {
    formateError: [],
    imageSizeError: [],
    videoSizeError: [],
    fileSizeError: [],
    audioSizeError: [],
    imageImportCountError: [],
    videoImportCountError: [],
    fileImportCountError: [],
    audioImportCountError: [],
    uploadError: [],
    checkSafeError: [],
  };

  return {
    imageList, videoList, fileList, errorList,
  };
};

/**
 * 验证文件格式和大小
 */
export const validateAndCategorizeFiles = (
  files: File[],
  imageAccept: string[],
  videoAccept: string[],
  fileAccept: string[],
  fileMaxSize: number,
  audioAccept: string[],
  imageMaxSize: number,
  videoMaxSize: number,
  audioMaxSize: number,
  errorList: ErrorListInfo,
  from?: 'canvas' | 'input' | 'workflow',
) => {
  const imageList: IFileList[] = [];
  const videoList: IFileList[] = [];
  const fileList: IFileList[] = [];
  const audioList: IFileList[] = [];

  // 非canvas和input来源
  const isNotCanvasAndInputFrom = from !== 'canvas' && from !== 'input';
  // const fileAcceptList = from === 'workflow' ? FILE_NO_AUDIO_TYPE_SUFFIX_MAP : FILE_TYPES;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const mimeType = typeof file?.type === 'string' ? file.type : '';
    if (!mimeType) {
      errorList.formateError.push(typeof file?.name === 'string' ? file.name : '');
      continue;
    }
    const isImageType = mimeType.startsWith(FileTypeEnum.Image);
    const isVideoType = mimeType.startsWith(FileTypeEnum.Video);
    const isAudioType = mimeType.startsWith(FileTypeEnum.Audio);
    // const isFileType = file.type.startsWith(FileTypeEnum.Document);
    const isFileType = FILE_TYPES.some((mime) => mimeType.startsWith(mime));

    if (!imageAccept.includes(file.type)
      && !videoAccept.includes(file.type)
      && !fileAccept.includes(file.type)
      && (isNotCanvasAndInputFrom ? !audioAccept.includes(file.type) : true)) {
      errorList.formateError.push(file.name);
      continue;
    }

    if (isImageType) {
      if (imageAccept.includes(file.type) && checkSize(file, imageMaxSize)) {
        imageList.push({
          file,
          type: FileTypeEnum.Image,
          suffix: getFileSuffix(file),
        });
      } else {
        errorList.imageSizeError.push(file.name);
      }
    }
    if (isVideoType) {
      if (videoAccept.includes(file.type) && checkSize(file, videoMaxSize)) {
        videoList.push({
          file,
          type: FileTypeEnum.Video,
          suffix: getFileSuffix(file),
        });
      } else {
        errorList.videoSizeError.push(file.name);
      }
    }
    if (isAudioType && isNotCanvasAndInputFrom) {
      console.log('audioList', file.type, audioAccept);
      if (audioAccept.includes(file.type) && checkSize(file, audioMaxSize)) {
        audioList.push({
          file,
          type: FileTypeEnum.Audio,
          suffix: getFileSuffix(file),
        });
      } else {
        errorList.audioSizeError.push(file.name);
      }
    }
    if (isFileType && from !== 'workflow') {
      console.log('fileList', file.type, fileAccept);
      if (fileAccept.includes(file.type) && checkSize(file, fileMaxSize)) {
        fileList.push({
          file,
          type: FileTypeEnum.Document,
          suffix: getDocumentSuffix(file),
        });
      } else {
        errorList.fileSizeError.push(file.name);
      }
    }
  }

  return {
    imageList, videoList, fileList, audioList,
  };
};

/**
 * 处理文件数量限制
 */
export const handleFileCountLimits = (
  imageList: IFileList[],
  videoList: IFileList[],
  fileList: IFileList[],
  audioList: IFileList[],
  imageImportCount: number,
  videoImportCount: number,
  fileImportCount: number,
  audioImportCount: number,
  errorList: ErrorListInfo,
) => {
  if (imageList.length > imageImportCount) {
    imageList.splice(imageImportCount).forEach((item) => {
      errorList.imageImportCountError.push(item.file.name);
    });
  }
  if (videoList.length > videoImportCount) {
    videoList.splice(videoImportCount).forEach((item) => {
      errorList.videoImportCountError.push(item.file.name);
    });
  }
  if (fileList.length > fileImportCount) {
    fileList.splice(fileImportCount).forEach((item) => {
      errorList.fileImportCountError.push(item.file.name);
    });
  }
  if (audioList.length > audioImportCount) {
    audioList.splice(audioImportCount).forEach((item) => {
      errorList.audioImportCountError.push(item.file.name);
    });
  }

  const spliceImage = imageList.splice(0, imageImportCount);
  const spliceVideo = videoList.splice(0, videoImportCount);
  const spliceFile = fileList.splice(0, fileImportCount);
  const spliceAudio = audioList.splice(0, audioImportCount);

  return {
    spliceImage, spliceVideo, spliceFile, spliceAudio,
  };
};

/**
 * 处理图片压缩
 */
export const processImages = async (
  spliceImage: IFileList[],
  imageMaxSide: number,
): Promise<IFileList[]> => {
  if (!spliceImage.length) return [];

  const processPromises = spliceImage.map(async ({ file, suffix }) => {
    // 先获取原始图片宽高，避免对超大图直接全尺寸绘制到 canvas
    const { width, height } = await getImgSizeByFile(file);

    // Canvas 不支持可靠地编码 GIF；保留原始文件，避免动画 GIF 被压缩或 EXIF 修复时损坏。
    if (file.type === 'image/gif') {
      return {
        type: FileTypeEnum.Image,
        suffix,
        file,
        width: width || 1024,
        height: height || 1024,
      };
    }

    // 如果图片任一边大于 imageMaxSide，走压缩逻辑：
    // compressImage 内部会根据 imageMaxSide 计算缩放后的宽高，并以该尺寸创建 canvas，
    // 从而避免 iOS Safari 上超大 canvas 带来的问题。
    if (isCompress(width, height, imageMaxSide)) {
      console.log('进入压缩');
      const { file: compressedFile } = await compressImage(file, imageMaxSide);
      return {
        type: FileTypeEnum.Image,
        suffix,
        file: compressedFile,
        width,
        height,
      };
    }

    console.log('未进入压缩');
    // 对于不需要压缩的小图，再做一次 EXIF 方向修复，风险较小
    const fixedFile = await removeExif(file);
    return {
      type: FileTypeEnum.Image,
      suffix,
      file: fixedFile,
      width: width || 1024,
      height: height || 1024,
    };
  });

  return Promise.all(processPromises);
};

/**
 * 处理视频文件
 */
export const processVideos = async (
  spliceVideo: IFileList[],
): Promise<IFileList[]> => {
  if (!spliceVideo.length) return [];

  const processPromises = spliceVideo.map(async ({ file, suffix }) => {
    // const { width, height } = await getVideoCoverSizeByFile(file);
    // if (isCompress(width, height, videoMaxSide)) {
    //   const { file: compressedFile } = await compressVideo(file, videoMaxSide);
    //   return { type: 'video', suffix, file: compressedFile };
    // }
    return { type: FileTypeEnum.Video, suffix, file };
  });

  return Promise.all(processPromises);
};

/**
 * 处理视频文件
 */
export const processFiles = async (
  spliceFile: IFileList[],
): Promise<IFileList[]> => {
  if (!spliceFile.length) return [];

  const processPromises = spliceFile.map(async ({ file, suffix }) => {
    // const { width, height } = await getVideoCoverSizeByFile(file);
    // if (isCompress(width, height, videoMaxSide)) {
    //   const { file: compressedFile } = await compressVideo(file, videoMaxSide);
    //   return { type: 'video', suffix, file: compressedFile };
    // }
    return { type: FileTypeEnum.Document, suffix, file };
  });

  return Promise.all(processPromises);
};

/**
 * 生成回调文件信息
 */
export const generateCallbackFiles = (allFileList: IFileList[]): {
  callbackFiles: Array<CallbackFiles>;
  filterFile: IFileList[];
} => {
  const callbackFiles: Array<CallbackFiles> = [];
  const filterFile = allFileList.filter(({
    file, suffix, width, height,
  }, index) => {
    const fileInfo = {
      index: String(index),
      type: formatFileType(file),
      name: getFileName(file),
      suffix,
      width,
      height,
      size: file.size,
    } as CallbackFiles;

    callbackFiles.push(fileInfo);
    return file;
  });

  return { callbackFiles, filterFile };
};

/**
 * 执行文件上传
 */
export const executeFileUpload = async (
  uploadInstance: Upload,
  filterFile: IFileList[],
  hideProgressMessage?: boolean,
  progressChange?: (data: number) => void,
): Promise<{
  imageUploadRes: { uri: string; url: string | undefined; }[];
  videoUploadRes: { uri: string; url: string | undefined; size?: number; }[];
  fileUploadRes: { uri: string; url: string | undefined; size?: number; }[];
  audioUploadRes: { uri: string; url: string | undefined; }[];
}> => {
  const uploadImagesFileArr = filterFile.filter((item) => item.type === FileTypeEnum.Image).map((f) => f.file);
  const uploadVideosFileArr = filterFile.filter((item) => item.type === FileTypeEnum.Video).map((f) => f.file);
  const uploadFilesArr = filterFile.filter((item) => item.type === FileTypeEnum.Document).map((f) => f.file);
  const uploadAudiosFileArr = filterFile.filter((item) => item.type === FileTypeEnum.Audio).map((f) => f.file);

  let imageUploadRes: { uri: string; url: string | undefined; }[] = [];
  let videoUploadRes: { uri: string; url: string | undefined; size?: number; }[] = [];
  let fileUploadRes: { uri: string; url: string | undefined; size?: number; }[] = [];
  let audioUploadRes: { uri: string; url: string | undefined; }[] = [];

  // sop节点后台上传配置，与roboneo节点无关
  const sopFlowSdkUploadConfig = useSopStore.getState().uploadConfig || {};

  if (uploadImagesFileArr.length) {
    imageUploadRes = await uploadInstance.uploadMultiple(uploadImagesFileArr, { ...sopFlowSdkUploadConfig, hideProgressMessage }, progressChange) || [];
  }
  if (uploadVideosFileArr.length) {
    const videoUploadList = await uploadInstance.uploadMultiple(uploadVideosFileArr, { ...sopFlowSdkUploadConfig, hideProgressMessage }, progressChange) || [];
    videoUploadRes = videoUploadList.map((item, index) => {
      const file = uploadVideosFileArr[index];
      return {
        ...item,
        ...(typeof file?.size === 'number' ? { size: file.size } : {}),
      };
    });
  }
  if (uploadFilesArr.length) {
    const documentUploadList = await uploadInstance.uploadMultiple(uploadFilesArr, { ...sopFlowSdkUploadConfig, hideProgressMessage }, progressChange) || [];
    fileUploadRes = documentUploadList.map((item, index) => {
      const file = uploadFilesArr[index];
      return {
        ...item,
        ...(typeof file?.size === 'number' ? { size: file.size } : {}),
      };
    });
  }
  if (uploadAudiosFileArr.length) {
    audioUploadRes = await uploadInstance.uploadMultiple(uploadAudiosFileArr, { ...sopFlowSdkUploadConfig, hideProgressMessage }, progressChange) || [];
  }

  return {
    imageUploadRes, videoUploadRes, fileUploadRes, audioUploadRes,
  };
};

/**
 * 处理上传结果
 */
export const processUploadResults = (
  uploadRes: { uri: string; url: string | undefined; size?: number; }[],
  callbackFiles: Array<CallbackFiles>,
  errorList: ErrorListInfo,
): {
  processedCallbackFiles: Array<CallbackFiles>;
  status: number;
  failImage: number;
  failVideo: number;
  failDocument: number;
  failAudio: number;
} => {
  const temporaryCallbackFiles = callbackFiles.concat();
  let status = 1; // 0-成功 1-失败 2-部分
  let failImage = callbackFiles.filter((f) => f.type === FileTypeEnum.Image).length;
  let failVideo = callbackFiles.filter((f) => f.type === FileTypeEnum.Video).length;
  let failDocument = callbackFiles.filter((f) => f.type === FileTypeEnum.Document).length;
  let failAudio = callbackFiles.filter((f) => f.type === FileTypeEnum.Audio).length;

  uploadRes?.filter((item, index) => {
    if (!item.url) {
      status = 2;
      errorList.uploadError.push(temporaryCallbackFiles[index].name);
      callbackFiles.splice(index, 1);
    }
    return item.url;
  }).forEach((item, index) => {
    console.log('item', item, callbackFiles[index]);
    let coverUrl = '';
    if (callbackFiles[index].type === FileTypeEnum.Video) {
      // 视频类型
      coverUrl = getVideoCoverUrl(item.url);
      failVideo -= 1;
    } else if (callbackFiles[index].type === FileTypeEnum.Audio) {
      failAudio -= 1;
    } else if (callbackFiles[index].type === FileTypeEnum.Image) {
      failImage -= 1;
    } else {
      failDocument -= 1;
    }

    callbackFiles.splice(index, 1, {
      ...callbackFiles[index],
      ...item,
      coverUrl,
    } as CallbackFiles);
  });

  return {
    processedCallbackFiles: callbackFiles, status, failImage, failVideo, failDocument, failAudio,
  };
};

// mediacheck 返回的 check_result key 在不同环境可能是完整 URL、去签名参数的 URL 或仅路径。
// 这里生成同一资源的常见 URL 形态，避免 dev/pre CDN 域名、query、编码差异导致误判。
const getSafetyCheckUrlVariants = (url: string) => {
  const rawUrl = url.trim();
  if (!rawUrl) return [];

  const variants = new Set<string>([rawUrl]);
  const addWithoutSearch = (value: string) => {
    const [withoutHash] = value.split('#');
    variants.add(withoutHash);
    variants.add(withoutHash.split('?')[0]);
  };

  addWithoutSearch(rawUrl);

  try {
    const parsedUrl = new URL(rawUrl);
    variants.add(parsedUrl.href);
    variants.add(`${parsedUrl.origin}${parsedUrl.pathname}`);
    variants.add(parsedUrl.pathname);
    variants.add(parsedUrl.pathname.replace(/^\//, ''));
    variants.add(decodeURIComponent(parsedUrl.pathname));
    variants.add(decodeURIComponent(parsedUrl.pathname).replace(/^\//, ''));
  } catch (error) {
    variants.add(rawUrl.replace(/^\//, ''));
  }

  try {
    variants.add(decodeURIComponent(rawUrl));
    addWithoutSearch(decodeURIComponent(rawUrl));
  } catch (error) {
    // Ignore malformed escape sequences and keep the original URL variants.
  }

  return Array.from(variants).filter(Boolean);
};

/**
 * 执行文件安审：将所有已上传文件 URL 通过 media_urls 提交 mediacheck，再校验文件名敏感词。
 */
export const performSafetyCheck = async (
  callbackFiles: Array<CallbackFiles>,
  errorList: ErrorListInfo,
): Promise<Array<CallbackFiles>> => {
  const mediaUrls = callbackFiles
    .filter((file) => typeof file.url === 'string' && file.url.length > 0)
    .map((file) => file.url);
  const checkResult = await checkFileApi({
    parameter: {
      path_scene: 'mediacheck',
      media_urls: mediaUrls,
    },
  });

  if (checkResult?.data?.error_code === 0) {
    const checkResultMap = checkResult.data.parameter?.check_result;
    // check_result 中出现的资源代表安审不通过；用归一化后的 URL 集合匹配上传回调 URL。
    const unsafeUrlSet = new Set(
      (checkResultMap && typeof checkResultMap === 'object' && !Array.isArray(checkResultMap)
        ? Object.keys(checkResultMap)
        : []
      ).flatMap(getSafetyCheckUrlVariants),
    );
    const safeFiles = callbackFiles.filter((item) => {
      const isUnsafe = getSafetyCheckUrlVariants(item.url).some((url) => unsafeUrlSet.has(url));
      if (isUnsafe) {
        errorList.checkSafeError.push(item.suffix ? `${item.name}.${item.suffix}` : item.name);
      }
      return !isUnsafe;
    });

    // 对通过媒体安审的文件，逐个校验文件名是否包含敏感内容
    const nameCheckedFiles = await Promise.all(
      safeFiles.map(async (file) => {
        try {
          const isSafe = await textSafeCheck(file.name);
          if (!isSafe) {
            return { ...file, name: '***' };
          }
        } catch {
          // 文件名安审接口异常时，兜底替换为安全名称
          return { ...file, name: '***' };
        }
        return file;
      }),
    );
    return nameCheckedFiles;
  }
  console.error('[performSafetyCheck] mediacheck failed', {
    checkResult,
    mediaUrls,
  });
  throw new Error('check file error');
};

/**
 * 对视频文件执行服务端压缩，返回原始 url 到 compressUrl 的映射。
 * 单文件压缩失败时降级为原始 url，不抛错。
 */
async function performVideoCompress(
  callbackFiles: Array<CallbackFiles>,
): Promise<Map<string, string>> {
  const videoFiles = callbackFiles.filter((f) => f.type === FileTypeEnum.Video && f.url);
  if (!videoFiles.length) {
    return new Map();
  }

  try {
    const compressResults = await Promise.allSettled(
      videoFiles.map(async (file) => {
        try {
          const { compress_url } = await videoCompress(file.url);
          return { url: file.url, compressUrl: compress_url || file.url };
        } catch {
          return { url: file.url, compressUrl: file.url };
        }
      }),
    );
    return new Map(
      compressResults
        .filter((r): r is PromiseFulfilledResult<{ url: string; compressUrl: string; }> => r.status === 'fulfilled')
        .map((r) => [r.value.url, r.value.compressUrl]),
    );
  } catch {
    return new Map(videoFiles.map((file) => [file.url, file.url]));
  }
}

/**
 * 合并安审结果与压缩结果：以安审后的文件列表为准，按原始 url 挂载 compressUrl。
 */
function mergePostUploadResults(
  checkedFiles: Array<CallbackFiles>,
  compressMap: Map<string, string> | null,
): Array<CallbackFiles> {
  if (!compressMap || compressMap.size === 0) {
    return checkedFiles;
  }

  return checkedFiles.map((file) => {
    if (file.type !== FileTypeEnum.Video) {
      return file;
    }
    const compressUrl = compressMap.get(file.url) ?? file.url;
    return { ...file, compressUrl };
  });
}

/**
 * 记录上传统计信息
 */
export const trackUploadStats = ({
  mediaType,
  status,
  imageTotal,
  videoTotal,
  audioTotal,
  documentTotal,
  callbackFiles,
  filterFile,
  uploadTime,
  fileFormat,
  errorMessage,
  nodeId,
  location,
}: {
  mediaType: string;
  status: number;
  imageTotal: number;
  videoTotal: number;
  audioTotal: number;
  documentTotal: number;
  callbackFiles: Array<CallbackFiles>;
  filterFile: IFileList[];
  uploadTime: number;
  fileFormat: string;
  errorMessage?: string;
  nodeId?: string;
  location?: string;
}) => {
  const searchParams = getUrlSearchParams();
  const room_id = searchParams.get('room_id');
  mtstatTrack('upload_complete', {
    room_id,
    node_id: nodeId || '',
    media_type: mediaType,
    status,
    image_total: imageTotal,
    video_total: videoTotal,
    audio_total: audioTotal,
    url: callbackFiles.map((i) => i.url).join(','),
    file_size: filterFile.map((item) => item.file.size).join(','),
    upload_time: uploadTime,
    file_format: fileFormat,
    error_message: errorMessage || (status === 2 ? '部分失败，sdk返回的数量为空' : ''),
    ...getUploadCompleteLocationTrackParams(location),
  });
};

/** 执行通用文件校验、上传和上传后处理，并允许业务在网络请求前过滤不合规文件。 */
export const dealUploadFile = async (filesInfo: FilesInfo, {
  callback,
  beforeUpload,
  changeBeforeUpload,
  getUploaderInstance,
  hideProgressMessage = false,
  progressChange,
  onUploadError,
  uploadHandler,
}: {
  callback: (files: Array<CallbackFiles>) => void;
  /** 在上传链路开始前过滤业务不接受的文件，避免无效文件进入对象存储。 */
  beforeUpload?: (files: File[]) => File[] | Promise<File[]>;
  changeBeforeUpload?: (file: File[]) => void;
  getUploaderInstance?: (uploadInstance: Upload) => void;
  hideProgressMessage?: boolean;
  progressChange?: (data: number) => void;
  onUploadError?: (errorList: ErrorListInfo, errorMessage: CallBackErrorMessage[]) => void;
  uploadHandler?: (fileList: IFileList[]) => Promise<{ uri: string; url: string | undefined; size?: number; }[]>;
}) => {
  const { env } = useOfficialStore.getState();
  // 初始化上传实例和配置
  const uploadInstance = Upload.getInstance();
  const {
    files: selectedFiles, controlImagesOptions = {},
    controlVideosOptions = {}, controlAudiosOptions = {},
    controlFilesOptions = {},
    isCheckSafe = true,
    isVideoCompress = false,
    formateErrorText,
    from,
    nodeId,
    location = 'agent_chat',
  } = filesInfo;
  let files = selectedFiles;
  const {
    accept: imageAccept = IMAGE_TYPES,
    maxSize: imageMaxSize = MAX_IMAGE_SIZE,
    maxSide: imageMaxSide = MAX_SIDE,
    importCount: imageImportCount = MAX_IMAGE_NUMS,
  } = controlImagesOptions;
  const {
    accept: videoAccept = VIDEO_TYPES,
    maxSize: videoMaxSize = MAX_VIDEO_SIZE,
    importCount: videoImportCount = MAX_VIDEO_NUMS,
  } = controlVideosOptions;

  const {
    accept: fileAccept = FILE_TYPES,
    maxSize: fileMaxSize = MAX_FILE_SIZE,
    importCount: fileImportCount = MAX_FILE_NUMS,
  } = controlFilesOptions;
  const {
    accept: audioAccept = AUDIO_TYPES,
    maxSize: audioMaxSize = MAX_AUDIO_SIZE,
    importCount: audioImportCount = MAX_VIDEO_NUMS,
  } = controlAudiosOptions;

  // debugger;

  // 初始化统计变量
  let imageTotal = files.filter((i) => i.type === FileTypeEnum.Image).length;
  let videoTotal = files.filter((i) => i.type === FileTypeEnum.Video).length;
  let audioTotal = files.filter((i) => i.type === FileTypeEnum.Audio).length;
  let documentTotal = files.filter((i) => i.type === FileTypeEnum.Document).length;
  let startTime = performance.now();
  let endTime = performance.now();
  let mediaType = [];
  if (imageTotal) {
    mediaType.push('image');
  }
  if (videoTotal) {
    mediaType.push('video');
  }
  if (documentTotal) {
    mediaType.push('file');
  }
  if (audioTotal) mediaType.push('audio');
  // let mediaType = mediaTypes.join(',');
  let errorStep = 'uploadError';
  let fileFormat: string[] = [];
  files.forEach((file) => {
    if (!fileFormat.includes(file.type)) {
      fileFormat.push(file.type);
    }
  });

  try {
    if (beforeUpload) {
      files = await beforeUpload(files);
      if (!files.length) {
        callback([]);
        return;
      }
    }

    // 初始化文件处理
    const { errorList } = initializeFileProcessing(files);
    console.log('files', files);

    // 执行文件预处理回调
    changeBeforeUpload?.(files);

    // 验证和分类文件
    const {
      imageList: validatedImageList, videoList: validatedVideoList, fileList: validatedFileList, audioList: validatedAudioList,
    } = validateAndCategorizeFiles(
      files,
      imageAccept,
      videoAccept,
      fileAccept,
      fileMaxSize,
      audioAccept,
      imageMaxSize,
      videoMaxSize,
      audioMaxSize,
      errorList,
      from,
    );

    console.log('筛选完格式及大小imageList', validatedImageList);
    console.log('筛选完格式及大小videoList', validatedVideoList);
    console.log('筛选完格式及大小audioList', validatedAudioList);
    console.log('筛选完格式及大小fileList', validatedFileList);
    // 处理文件数量限制
    const {
      spliceImage, spliceVideo, spliceFile, spliceAudio,
    } = handleFileCountLimits(
      validatedImageList,
      validatedVideoList,
      validatedFileList,
      validatedAudioList,
      imageImportCount,
      videoImportCount,
      fileImportCount,
      audioImportCount,
      errorList,
    );
    progressChange && progressChange(5);

    // 处理图片压缩
    const processedImageList = await processImages(spliceImage, imageMaxSide);

    // 处理视频文件
    const processedVideoList = await processVideos(spliceVideo);
    // 处理文件
    const processedFileList = await processFiles(spliceFile);

    // 处理音频文件（音频不需要压缩，直接使用）
    const processedAudioList = spliceAudio;
    progressChange && progressChange(15);
    // 生成回调文件信息
    const allFileList = [...processedImageList, ...processedVideoList, ...processedFileList, ...processedAudioList];
    const { callbackFiles, filterFile } = generateCallbackFiles(allFileList);

    // 初始化上传实例
    uploadInstance.init({ debug: env === 'development' });

    // 检查是否有文件需要上传
    if (!filterFile.length) {
      getUploadErrorMessage(errorList, {
        controlImagesOptions: {
          importCount: imageImportCount,
          maxSize: imageMaxSize,
          maxTotal: controlImagesOptions.maxTotal,
          currentVideoCount: controlImagesOptions.currentVideoCount,
        },
        controlVideosOptions: {
          importCount: videoImportCount,
          maxSize: videoMaxSize,
        },
        controlAudiosOptions: {
          importCount: audioImportCount,
          maxSize: audioMaxSize,
        },
        controlFilesOptions: {
          importCount: fileImportCount,
          maxSize: fileMaxSize,
        },
        formateErrorText,
      }, String(uploadInstance.uploadTaskId), onUploadError, {
        location,
        from,
        nodeId,
        failurePhase: 'validation_no_upload',
      });
      callback([]);
      return;
    }

    // 设置上传实例和进度
    getUploaderInstance?.(uploadInstance);
    const total = filterFile.length;

    setUploadThread(String(uploadInstance.uploadTaskId), {
      hide: hideProgressMessage,
      total,
      done: 0,
    });

    // 更新统计信息
    const uploadImagesFileArr = filterFile.filter((item) => item.type === FileTypeEnum.Image).map((f) => f.file);
    const uploadVideosFileArr = filterFile.filter((item) => item.type === FileTypeEnum.Video).map((f) => f.file);
    const uploadDocumentFileArr = filterFile.filter((item) => item.type === FileTypeEnum.Document).map((f) => f.file);
    const uploadAudiosFileArr = filterFile.filter((item) => item.type === FileTypeEnum.Audio).map((f) => f.file);
    imageTotal = uploadImagesFileArr.length;
    videoTotal = uploadVideosFileArr.length;
    documentTotal = uploadDocumentFileArr.length;
    audioTotal = uploadAudiosFileArr.length;
    fileFormat = [];
    filterFile.forEach((file) => {
      if (!fileFormat.includes(file.suffix)) {
        fileFormat.push(file.suffix);
      }
    });
    mediaType = [];
    if (imageTotal) {
      mediaType.push('image');
    }
    if (videoTotal) {
      mediaType.push('video');
    }
    if (documentTotal) {
      mediaType.push('file');
    }
    if (audioTotal) {
      mediaType.push('audio');
    }
    // 执行文件上传
    startTime = performance.now();
    let uploadRes = [];
    if (uploadHandler) {
      uploadRes = await uploadHandler(filterFile);
    } else {
      const {
        imageUploadRes, videoUploadRes, fileUploadRes, audioUploadRes,
      } = await executeFileUpload(uploadInstance, filterFile, hideProgressMessage, progressChange);
      endTime = performance.now();

      // 处理上传结果
      uploadRes = [...imageUploadRes || [], ...videoUploadRes || [], ...fileUploadRes || [], ...audioUploadRes || []];
    }

    const {
      processedCallbackFiles: finalCallbackFiles,
      status: uploadStatus,
      failImage,
      failVideo,
      failAudio,
    } = processUploadResults(uploadRes, callbackFiles, errorList);

    console.log('callbackFiles', finalCallbackFiles);

    // 检查上传结果
    if (!finalCallbackFiles.length) {
      trackUploadStats({
        mediaType: mediaType.join(','),
        status: 1,
        imageTotal,
        videoTotal,
        audioTotal,
        documentTotal,
        callbackFiles: [],
        filterFile,
        uploadTime: endTime - startTime,
        fileFormat: fileFormat.join(','),
        errorMessage: 'sdk返回的数量为空',
        nodeId,
        location,
      });
      removeThread(String(uploadInstance.uploadTaskId));
      getUploadErrorMessage(errorList, {
        controlImagesOptions: {
          importCount: imageImportCount,
          maxSize: imageMaxSize,
          maxTotal: controlImagesOptions.maxTotal,
          currentVideoCount: controlImagesOptions.currentVideoCount,
        },
        controlVideosOptions: {
          importCount: videoImportCount,
          maxSize: videoMaxSize,
        },
        controlFilesOptions: {
          importCount: fileImportCount,
          maxSize: fileMaxSize,
        },
        formateErrorText,
        controlAudiosOptions: {
          importCount: audioImportCount,
          maxSize: audioMaxSize,
        },
      }, String(uploadInstance.uploadTaskId), onUploadError, {
        location,
        from,
        nodeId,
        failurePhase: 'upload_all_failed',
        uploadTimeMs: endTime - startTime,
        filterFileSummary: buildFilterFileBrief(filterFile),
        uploadResSummary: buildUploadResBrief(uploadRes),
      });
      callback([]);
      return;
    }

    // 计算最终状态
    const finalStatus = finalCallbackFiles.length === filterFile.length ? 0 : uploadStatus;

    // 记录上传统计
    trackUploadStats({
      mediaType: mediaType.join(','),
      status: finalStatus,
      imageTotal: finalStatus === 0 ? imageTotal : imageTotal - failImage,
      videoTotal: finalStatus === 0 ? videoTotal : videoTotal - failVideo,
      audioTotal: finalStatus === 0 ? audioTotal : audioTotal - failAudio,
      nodeId,
      documentTotal,
      callbackFiles: finalCallbackFiles,
      filterFile,
      uploadTime: endTime - startTime,
      fileFormat: fileFormat.join(','),
      errorMessage: finalStatus === 2 ? '部分失败，sdk返回的数量为空' : '',
      location,
    });

    // 安审与视频压缩并行执行，按配置跳过未开启的流程
    const needSafetyCheck = isCheckSafe && !useSopStore.getState().fromAdmin;
    const needVideoCompress = isVideoCompress
      && finalCallbackFiles.some((f) => f.type === FileTypeEnum.Video && f.url);

    let safeCallbackFiles = finalCallbackFiles;
    if (needSafetyCheck || needVideoCompress) {
      if (needSafetyCheck) {
        errorStep = 'checkStep';
      }

      const [checkedFiles, compressMap] = await Promise.all([
        needSafetyCheck
          ? performSafetyCheck(finalCallbackFiles, errorList)
          : Promise.resolve(finalCallbackFiles),
        needVideoCompress
          ? performVideoCompress(finalCallbackFiles)
          : Promise.resolve(null),
      ]);

      safeCallbackFiles = mergePostUploadResults(checkedFiles, compressMap);
      if (needSafetyCheck) {
        console.log('checkResult completed');
      }
    }

    // 完成上传
    setUploadThread(String(uploadInstance.uploadTaskId), {
      hide: hideProgressMessage,
      done: total,
      detected: true,
    });

    console.log('last errorList...', errorList);
    callback(safeCallbackFiles);
    getUploadErrorMessage(errorList, {
      controlImagesOptions: {
        importCount: imageImportCount,
        maxSize: imageMaxSize,
        maxTotal: controlImagesOptions.maxTotal,
        currentVideoCount: controlImagesOptions.currentVideoCount,
      },
      controlVideosOptions: {
        importCount: videoImportCount,
        maxSize: videoMaxSize,
      },
      controlFilesOptions: {
        importCount: fileImportCount,
        maxSize: fileMaxSize,
      },
      formateErrorText,
      controlAudiosOptions: {
        importCount: audioImportCount,
        maxSize: audioMaxSize,
      },
    }, String(uploadInstance.uploadTaskId), onUploadError, {
      location,
      from,
      nodeId,
      failurePhase:
        // eslint-disable-next-line no-nested-ternary
        errorList.uploadError.length > 0
          ? (finalStatus === 2 ? 'upload_partial' : 'unknown')
          : (errorList.checkSafeError.length > 0 ? 'post_check_safe' : 'unknown'),
      uploadTimeMs: endTime - startTime,
      filterFileSummary: buildFilterFileBrief(filterFile),
      uploadResSummary: buildUploadResBrief(uploadRes),
    });
  } catch (error) {
    console.log('upload error===', error);
    message.error('RoboNeo_Web_upload_fail_text');
    endTime = performance.now();

    reportUploadFailureToSentry('[RoboNeo] dealUploadFile exception', {
      diagnostic: {
        location,
        from,
        nodeId,
        failurePhase: 'exception',
        uploadTimeMs: endTime - startTime,
      },
      caughtError: error,
    });

    // 记录错误统计
    if (errorStep === 'uploadError') {
      trackUploadStats({
        mediaType: mediaType.join(','),
        status: 1,
        imageTotal,
        videoTotal,
        audioTotal,
        documentTotal,
        callbackFiles: [],
        filterFile: [],
        uploadTime: endTime - startTime,
        fileFormat: fileFormat.join(','),
        errorMessage: String(error),
        nodeId,
        location,
      });
    }

    // 取消上传线程
    setUploadThread(String(uploadInstance.uploadTaskId), {
      hide: hideProgressMessage,
      canceled: true,
    });

    callback([]);
  }
};

export function canvasToBlob(canvas: HTMLCanvasElement, type?: string | undefined, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(blob);
        }
      }, type, quality);
    } catch (e) {
      console.log('canvasToBlob error', e);
      reject(e);
    }
  });
}
/**
 * 将 Blob 转为File
 *
 * @export
 * @param {Blob} blob
 * @param {string} name
 * @return {*}  {File}
 */
export function blobToFile(blob: Blob, name: string): File {
  const { type } = blob;
  return new File([blob], name, { type });
}
// url转Blob
export async function urlToBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
  }
  const blob = await response.blob();

  return blob;
}
// 是否压缩
export const isCompress = (width: number, height: number, maxSide: number) => {
  // width height 为0时，说明图片过大，ios上无法加载
  return width > maxSide || height > maxSide;
};
// 压缩图片最长边至 maxSide（默认 MAX_SIDE）
type ImageInput = File | Blob | string; // File, Blob, URL, Base64

export const compressImage = (
  input: ImageInput,
  maxSide: number,
): Promise<{
  file: File;
  blob: Blob;
  width: number;
  height: number;
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // 允许跨域图片处理（前提是后端允许 CORS）
    img.crossOrigin = 'anonymous';

    // 生成 objectURL 或直接使用 URL/dataURL
    if (typeof input === 'string') {
      img.src = input;
    } else {
      img.src = URL.createObjectURL(input);
    }

    img.onload = () => {
      let { width, height } = img;

      if (width > height) {
        if (width > maxSide) {
          height = (height * maxSide) / width;
          width = maxSide;
        }
      } else if (height > maxSide) {
        width = (width * maxSide) / height;
        height = maxSide;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      console.log('compress after', 'width', width, 'height', height);
      canvasToBlob(canvas, (input as File).type || 'image/png', 1).then((blob) => {
        console.log('compress after', 'blob', blob);
        if (blob) {
          const filename = typeof input === 'string' ? 'compressed.png' : (input as File).name;
          resolve({
            file: blobToFile(blob, filename),
            width,
            height,
            blob,
          });
        } else {
          reject(new Error('Canvas compression failed'));
        }
      });
    };

    img.onerror = (err) => {
      reject(new Error('Failed to load image for compression'));
    };
  });
};

/**
 * 编辑器前置上传：复用通用上传链路，并允许调用方补充埋点 location。
 */
export function uploadFileBeforeEditor(
  fileList: File[],
  isCheckSafe?: boolean,
  location?: LocationOptions,
): Promise<CallbackFiles[]> {
  return new Promise((resolve, reject) => {
    dealUploadFile({
      files: fileList,
      controlImagesOptions: {
        maxSize: Infinity,
        maxSide: Infinity,
      },
      controlVideosOptions: {
        maxSize: Infinity,
        maxSide: Infinity,
      },
      isCheckSafe: isCheckSafe ?? false,
      location,
    }, {
      callback: async (uploadData) => {
        if (!uploadData.length) {
          reject(uploadData);
          return;
        }
        resolve(uploadData);
      },
    });
  });
}
